/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';

import { Checkbox, DividerNew, Heading, HelpTip } from 'sarsaparilla';

import { FiltersTypeProps } from '../../../types/filters';
import styles from './CommonFilter.module.scss';

export type CommonFilterProps = {
    /** Selected filter groups */
    fg: string[];
    title: string;
    filters: Array<FiltersTypeProps>;
    onChange?: (v: React.ChangeEvent<HTMLInputElement>, k: FiltersTypeProps) => void;
    columns?: number;
    hasDivider?: boolean;
    dividerClass?: string;
    clickTagCategory?: string;
    headingLevel?: 3 | 2 | 1 | 4 | 5 | 6 | undefined;
    /** Filter keys whose checkboxes can't be changed */
    disabledKeys?: string[];
    /** Text shown under an option's label, by filter key */
    descriptions?: { [key: string]: string };
};

/**
 * Checkboxes for a group of filter keys stored in `fg`. Every option can be
 * selected unless it's in `disabledKeys`.
 */
function CommonFilterBase({
    fg,
    title,
    filters,
    onChange,
    columns,
    hasDivider = true,
    dividerClass,
    clickTagCategory = 'Global Search Filters',
    headingLevel = 3,
    disabledKeys,
    descriptions,
}: CommonFilterProps) {
    if (!filters || filters.length === 0) {
        return null;
    }

    const className =
        columns === 1
            ? 'search-filters-single-type-wrap'
            : 'search-filters-double-type-wrap';

    return (
        <div className="search-common-filters__container">
            <Heading className="mb-2 mt-0" headingLevel={headingLevel} appearance="h6">
                {title}
            </Heading>
            <div className={className} role="group" aria-label={`${title} filters`}>
                <legend className="rec-sr-only">Select {title} to filter on</legend>
                {filters.map((item) => {
                    const description = descriptions?.[item.key];
                    const descriptionId = description
                        ? `${item.key}-description`
                        : undefined;

                    return (
                        <div
                            className={cx('search-filter-check-item', {
                                hasIcon: item.icon,
                            })}
                            key={item.key}
                        >
                            <Checkbox
                                onChange={(e) => onChange?.(e, item)}
                                id={item.key}
                                label={item.value}
                                value={item.key}
                                isChecked={fg.includes(item.key)}
                                isDisabled={disabledKeys?.includes(item.key)}
                                aria-describedby={descriptionId}
                                clickTagCategory={clickTagCategory}
                                clickTagAction={`${title}: ${item.key}`}
                                clickTagLabel={`${item.value} Filter`}
                            />
                            {description && (
                                <div
                                    id={descriptionId}
                                    className={cx(
                                        'text-sm color-text-03',
                                        styles.description
                                    )}
                                >
                                    {description}
                                </div>
                            )}
                            {item.icon && item.icon}
                            {item.accessibility && (
                                <span className="search-accessible-camping">
                                    <HelpTip
                                        appearance="info"
                                        content="Accessible Site: This site has features that provide better accessibility than others.  Such features may include clear ground space, a firm surface and other features.  We are in the process of examining features that improve accessibility and welcome your feedback."
                                    />
                                </span>
                            )}
                        </div>
                    );
                })}
                {hasDivider && (
                    <div
                        className={cx('search-common-filter__divider', dividerClass, {
                            'pt-2 pb-2': !dividerClass,
                        })}
                    >
                        <DividerNew />
                    </div>
                )}
            </div>
        </div>
    );
}

export const CommonFilter = React.memo(CommonFilterBase, (prevProps, nextProps) => {
    return (
        prevProps.fg === nextProps.fg &&
        prevProps.title === nextProps.title &&
        prevProps.onChange === nextProps.onChange &&
        isEqual(prevProps.filters, nextProps.filters) &&
        isEqual(prevProps.disabledKeys, nextProps.disabledKeys) &&
        isEqual(prevProps.descriptions, nextProps.descriptions)
    );
});
